import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { modals } from '@/mixins/modal';
import { queuedWork } from '@/mixins/queued-work';
import { recycleDroplet } from '@/mixins/recycle-droplet';
import { editHistory } from '@/mixins/history';
import { removeDropletHook } from '@/mixins/remove-droplet';
import { siteBuilder } from '@/mixins/site-builder';
import { diff } from 'deep-object-diff';
import { guid } from '@/mixins/guid';
import droplets from '@editor/droplets/droplets.config.json';

export default Vue.component('droplet-options', {
  template: require('./template.html'),
  mixins: [
    modals,
    guid,
    recycleDroplet,
    siteBuilder,
    removeDropletHook,
    editHistory,
    queuedWork,
  ],
  computed: {
    ...mapGetters([
      'showDroplets',
      'shareableList',
      'getSharedDroplets',
      'currentPage',
      'currentSite',
      'initialCurrentPage',
      'viewport',
      'activeDroplet',
      'droppedId',
    ]),
    canvasEditor() {
      return this.currentPage._id === this.droplet._id;
    },
    optionsNeeded() {
      // roamingTemplate = a droplet or section that belongs to a template but user is not editing
      // that template. They are looking at the item(s) in a page or draft or somewhere else.
      return (
        !this.roamingTemplate() ||
        (!this.canvasEditor && !this.roamingTemplate()) ||
        (this.canvasEditor && !this.roamingTemplate())
      );
    },
    hasStyles() {
      // list of droplets that do NOT have styling
      const drp = this.droplet.type;
      return drp !== 'droplet.codeGeneral';
    },
    hasSettings() {
      // list of droplets that do NOT have settings
      const drp = this.droplet.type;
      return drp !== 'droplet.header' &&
      drp !== 'droplet.divider' &&
      drp !== 'droplet.textGeneral';
    },
    inBlocks() {
      return _.isNumber(this.blockIndex);
    },
    canDrag() {
      const drp = this.droplet;
      const isNav = drp.type === 'droplet.navigation';
      const isFixed = drp.scrollType === 'fixed';
      return (isNav && !isFixed) || !isNav;
    },
    settingsClass() {
      const typeString = this.droplet.type;
      const type = typeString.split('.')[1];
      const fallbackClass = 'expandDroplet';
      return type ? `droplet-icon-${type}` : fallbackClass;
    },
    droplets() {
      // we need this so this computed property can observe it.
      const exists = this.currentSite.recycled;
      const list = exists ? _.filter(exists, drp => drp.type === this.droplet.type) : [];
      const sortOn = this.sortKey || this.cols[0];
      return _.chain(list)
        .thru((t) => {
          if (this.filterKey) {
            return _.reduce(
              t,
              (acc, row) => {
                const e = [];
                // only grab the properties that the table uses
                _.filter(row, () => {
                  _.forEach(this.cols, (c) => {
                    // clean up the values so that the
                    // searchable items are like how
                    // the template is written
                    const col = this.cleanItem(row[c], c);
                    e.push(col);
                  });
                });
                if (
                  _.filter(e, v =>
                    _(v)
                      .toLower()
                      .includes(this.filterKey.toLowerCase()),
                  ).length
                ) {
                  acc.push(row);
                }
                return acc;
              },
              [],
            );
          }
          return t;
        })
        .sort((a, b) => {
          // We'll sort accending by default and we'll also ignore case
          // since that may confuse non-technical users
          const ascDesc = this.sortMap[sortOn];
          a = _.isString(a[sortOn]) ? _.toLower(a[sortOn]) : a[sortOn]; // eslint-disable-line
          b = _.isString(b[sortOn]) ? _.toLower(b[sortOn]) : b[sortOn]; // eslint-disable-line
          return (a === b ? 0 : a < b ? 1 : -1) * ascDesc; // eslint-disable-line
        })
        .value();
    },
  },
  data() {
    const cols = ['name', 'createdDate'];
    const sortMap = _.zipObject(cols, _.fill(Array(cols.length), -1));
    return {
      cols,
      loading: false,
      sortKey: '',
      filterKey: '',
      activeRecycled: null,
      sortMap,
      paginate: ['droplets'],
      offScreenRight: false,
      offScreenLeft: false,
      showArrow: false,
      bubble: false,
      confirmReplace: false,
      showRecycleModal: false,
      showConfirmModal: false,
      showErrorModal: false,
      showSaveModal: false,
      showEditBtns: false,
      showTemplateTip: false,
      initialState: null,
      zIndex: 3,
      recyclename: '',
      confirmDel: false,
      bubbleStatus: this.showEditBubble,
      cancelling: false,
      mobileOpenClass: '',
    };
  },
  name: 'droplet-options',
  props: [
    'block-index',
    'droplet-hovered',
    'set-bubble-size',
    'show-edit-bubble',
    'items',
    'index',
    'droplet',
    'descendants',
  ],
  created() {
    this.initialState = this.getInitialState();
  },
  mounted() {
    this.onDrop(this.droppedId);
  },
  watch: {
    showEditBubble(open) {
      this.toggleEditBubble(open);
      if (open) {
        this._scrollToDroplet(this.$el);
      }
    },
    setBubbleSize() {
      this.adjustBubbleForViewport(200);
    },
    viewport() {
      this.adjustBubbleForViewport(200);
    },
    showRecycleModal(open) {
      if (open) {
        this.recyclename = '';
      }
      this.$store.dispatch('setGlobalOpenModal', open);
    },
    showConfirmModal(open) {
      this.$store.dispatch('setGlobalOpenModal', open);
    },
    showErrorModal(open) {
      this.$store.dispatch('setGlobalOpenModal', open);
    },
    showSaveModal(open) {
      this.$store.dispatch('setGlobalOpenModal', open);
    },
    droppedId(_id) {
      this.onDrop(_id);
    },
  },
  methods: {
    onDrop(_id) {
      if (this.droplet._id === _id) {
        this._scrollToDroplet(this.$parent.$el);
        this.$store.dispatch('setDroppedId', null);
      }
    },
    toggleEditBubble(open) {
      // this will move an overlay from inside the bubble to outside app body.
      // so we can attach click events to the new overlay that'll revert changes
      // and close the open bubble. A much better approach than closing all bubbles
      const overlay = this.$refs.drpOverlay;
      const wrapper = this.$refs.drpOverlayWrapper;
      const app = document.getElementById('drzBubble-overlay-container');
      this.bubbleStatus = open;
      if (open) {
        // if there happens to be open droplet bubbles from other droplets, we need
        // to close them all.
        if (app.hasChildNodes()) {
          _.forEach(app.childNodes, (child) => {
            child.click();
          });
        }
        this.$store.dispatch('setGlobalBubbleStatus', true);
        app.appendChild(overlay);
        this.initialState = this.getInitialState();
        // if openning a bubble, close open droplets tab for more space
        if (this.showDroplets) {
          this.$store.dispatch('toggleDroplets', false);
        }
      } else if (!_.isUndefined(wrapper)) {
        // move back
        wrapper.appendChild(overlay);
      }
      if (!open) {
        this.$store.dispatch('setGlobalBubbleStatus', false);
        this.setEditHistory({
          type: 'droplet_settings',
          meta: {
            dropletType: this.droplet.type,
          },
        });
      }
    },
    roamingTemplate() {
      return this.currentPage.type !== 'template' && this.droplet.templateRegion;
    },
    hoverDropletOptions() {
      this.zIndex = 20;
      this.$store.dispatch('setGlobalOptionsHover', true);
    },
    offDropletOptions() {
      this.showEditBtns = false;
      this.showTemplateTip = false;
      this.zIndex = 3;
      this.$store.dispatch('setGlobalOptionsHover', false);
    },
    async goToTemplate() {
      const cP = this._deepOmit(this.currentPage, this._pageDiffIgnore);
      const iP = this._deepOmit(this.initialCurrentPage, this._pageDiffIgnore);
      if (_.isEqual(cP, iP)) {
        await this.setCurrentPage();
      } else {
        // there are unsaved changes
        this.toggleModal('Save');
        this.$log.info('Unsaved Page Difference: ', diff(cP, iP));
      }
    },
    async setCurrentPage() {
      const template = _.find(this.currentSite.templates, { _id: this.currentPage.templateID });
      const { item } = await this.checkQueuedWork({ page: template });
      this.$store.dispatch('setOriginalPage', item);
      return this.$store.dispatch('setCurrentPage', item);
    },
    async checkPage(choice) {
      // This should never run on a template or a recycled droplet, only pages and drafts
      // since this is triggered when clicking on a template droplet in a page or draft
      if (choice === 'yes') {
        // revert the droplet count to the initial value
        await this.$store.dispatch('revertDropletCounts', {
          site: this.currentSite,
        });
        // revert all page data to the initial value
        await this.$store.dispatch('revertPage', this.initialCurrentPage);
        this.$store.dispatch('updateInitialPage');
        this.toggleModal('Save');
        this.setCurrentPage();
      }
      if (choice === 'no') {
        this.toggleModal('Save');
      }
    },
    confirmReplaceDrp(droplet) {
      this.activeRecycled = droplet;
      this.confirmReplace = true;
    },
    async preLoadRecycled(droplet) {
      await this.routeRecycleAction({
        action: 'loadFromRecycled',
        newId: this.droplet._id, // new id has been generated when dropped
        type: this._dropletType(droplet),
        droplet,
      });
      droplet._id = this.droplet._id;
      return droplet;
    },
    async recycleLoadDroplet() {
      this.loading = true;
      const items = this.items;
      const index = this.index;
      try {
        this.toggleModal('Recycle');
        await this._timeBuffer(300);
        const droplet = await this.preLoadRecycled(_.cloneDeep(this.activeRecycled));
        this.$store.dispatch('runStateFn', {
          fn() {
            items.splice(index, 1);
          },
        });
        setTimeout(() => {
          this.$store.dispatch('runStateFn', {
            fn() {
              items.splice(index, 0, droplet);
            },
          });
          this.setEditHistory({
            type: 'load_recycled',
            meta: {
              dropletType: this.droplet.type,
            },
          });
        }, 250);
      } catch (e) {
        this.$log.error(e);
      } finally {
        this.loading = false;
        this.confirmReplace = false;
        this._trackEvent({
          type: 'use_recycled_droplet',
          meta: {
            droplet_type: this.droplet.type,
            site_name: this.currentSite.alias,
            site_type: this.currentSite.type,
            site_tier: this.currentSite.tier,
            page_name: this.currentPage.name,
            page_url: this.currentPage.url,
          },
        });
      }
    },
    toggleModal(type) {
      const modal = `show${type}Modal`;
      this[modal] = !this[modal];
      const content = this.$refs[`drp${type}Modal`];
      if (this[modal]) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
      if (type === 'Recycle' && this.bubbleStatus) {
        this.revertDroplet();
        this.toggleBubble();
      }
    },
    setSortKey(k) {
      this.sortKey = k;
      // Invert the value for ascDesc
      this.sortMap[k] = this.sortMap[k] * -1;
    },
    getInitialState() {
      // we use this to replace droplet on cancel
      return _.cloneDeep(this.droplet);
    },
    getDropletName() {
      const name = this._dropletType(this.droplet).name;
      return droplets[name].label;
    },
    getType() {
      let type = '';
      if (!_.isUndefined(this.droplet)) {
        type = this.droplet.type.split('.')[0];
      }
      return type;
    },
    queueS3Deletions() {
      // check if droplet has a unique s3 folder, if so, queue it up to delete it. this
      // is typically only for image type droplets where images are not shared across
      // other droplets like cards and sections do it
      if (this.currentPage.type !== 'draft') {
        const name = this._dropletType(this.droplet).name;
        const droplet = droplets[name];
        const hasIdFolder = droplet.storage ? droplet.storage.idFolder : false;
        if (hasIdFolder) {
          this.$store.dispatch('updateS3Queue', {
            type: name,
            _id: this.droplet._id,
          });
        }
      }
    },
    removeDroplet() {
      // Note: This will only remove the droplet from the page/template that's it's on
      const payload = {};
      payload.index = this.index;
      payload.items = this.items;
      const overlay = this.$refs.drpOverlay;
      const wrapper = this.$refs.drpOverlayWrapper;
      // if there is an overlay open when removing; ie: removing drp when a bubble is open
      if (this.bubbleStatus) {
        wrapper.appendChild(overlay);
      }
      // check if it droplet has any s3 content
      this.queueS3Deletions();
      // run remove droplet hooks
      this.removeDropletHook(this.droplet);
      // close modal
      this.toggleModal('Confirm');
      // we utilize the timeout for smooth animation. we wait for the close modal effect
      // and DOM moving before we actually remove the drp from the page.
      setTimeout(async () => {
        const c = this.$children;
        await this.$store.dispatch('removeDroplet', payload);
        this.setEditHistory({
          type: 'remove_droplet',
          meta: {
            dropletType: this.droplet.type,
          },
        });
        this.afterActionCheck(c, 'drzAfterRemove');
      }, 500);

      this._trackEvent({
        type: 'remove_droplet',
        meta: {
          droplet_type: this.droplet.type,
          site_name: this.currentSite.alias,
          site_type: this.currentSite.type,
          site_tier: this.currentSite.tier,
          page_name: this.currentPage.name,
          page_url: this.currentPage.url,
        },
      });
    },
    afterActionCheck(c, fn) {
      function getChildren(ch) {
        _.forEach(ch, (item) => {
          if (ch.length > 0) {
            const child = item;
            const method = child[fn];
            if (!_.isUndefined(method)) {
              // init the callback
              method();
            }
            getChildren(child.$children);
          }
        });
      }
      getChildren(c);
    },
    toggleBubble() {
      return new Promise((resolve) => {
        this.$store.dispatch('setActiveDroplet', { droplet: {}, items: [] });
        this.$emit('toggle-bubble');
        resolve();
      });
    },
    async onCancel() {
      await this.toggleBubble();
      this.revertDroplet();
    },
    toggleSideBar() {
      if (this.showEditBubble) {
        this.$emit('toggle-bubble');
      }
      const droplet = !this.activeDroplet._id ? this.droplet : {};
      this.$store.dispatch('setActiveDroplet', {
        droplet,
        items: this.items,
        index: this.index,
      });
    },
    offScreen(el) {
      const c = el.getBoundingClientRect();
      const rightCheck = c.left + c.width;
      const leftCheck = c.right - c.width;
      if (rightCheck > window.innerWidth) {
        this.offScreenRight = true;
        this.offScreenLeft = false;
      }
      if (leftCheck < 0) {
        this.offScreenRight = false;
        this.offScreenLeft = true;
      }
    },
    adjustBubbleForViewport(delay) {
      // this will add a class to center a wide bubble for mobile previews
      if (this.viewport === 'mobile') {
        const bubble = this.$refs.bubbleDiv;
        const bubbleParent = bubble.parentNode;
        setTimeout(() => {
          if (bubble.offsetWidth > bubbleParent.offsetWidth) {
            this.mobileOpenClass = 'dropletBubble-afterShow';
          } else {
            this.mobileOpenClass = '';
          }
        }, delay || 0);
      }
    },
    afterEnter(el) {
      this.adjustBubbleForViewport();
      this.offScreen(el);
      this.showArrow = true;
    },
    afterLeave(el) {
      this.mobileOpenClass = '';
      this.offScreen(el);
      this.showArrow = false;
    },
    async preSaveNewRecycled(droplet) {
      const newId = this.guid();
      await this.routeRecycleAction({
        action: 'saveNewRecycled',
        newId,
        type: this._dropletType(droplet),
        droplet,
      });
      droplet.name = this.recyclename;
      droplet._id = newId;
      return droplet;
    },
    async recycleSave() {
      this.$validator.validateAll().then(async (result) => {
        if (result) {
          this.loading = true;
          const n = {
            id: this.guid(),
            type: 'success',
            message: `Successfully created new droplet: ${this.recyclename}!`,
          };
          try {
            // if site does not have recycled property yet, add it
            if (!this.currentSite.recycled) {
              this.$store.dispatch('setGlobalNewValues', {
                vm: this,
                item: this.currentSite,
                values: [
                  {
                    key: 'recycled',
                    value: [],
                  },
                ],
              });
            }
            const droplet = await this.preSaveNewRecycled(_.cloneDeep(this.droplet));
            this.$store.dispatch('setGlobalValue', {
              array: true,
              item: this.currentSite,
              key: 'recycled',
              value: droplet,
            });
            await this._SEND([{
              name: 'updateSiteAttr',
              payload: {
                siteId: this.currentSite._id,
                attr: 'recycled',
                value: this.currentSite.recycled,
              },
            }]);
            this.$log.debug('successfully updated recycled');
            this.toggleModal('Recycle');
          } catch (e) {
            this.$log.error(e);
            n.type = 'error';
            n.message = `${e.message || e}. This has been reported.`;
            await this._reportError({
              error: e,
              fn: 'recycleSave',
            });
          } finally {
            this.loading = false;
            this.$store.dispatch('addNotification', n);
            this._trackEvent({
              type: 'new_recycled_droplet',
              meta: {
                droplet_type: this.droplet.type,
                site_name: this.currentSite.alias,
                site_type: this.currentSite.type,
                site_tier: this.currentSite.tier,
                page_name: this.currentPage.name,
                page_url: this.currentPage.url,
              },
            });
          }
        }
      });
    },
    showPuddleHelpers() {
      const $parent = this.$parent.$el;
      const $puddle = $parent.querySelectorAll('[drp-level]')[0];
      const time = !this.showDroplets ? 200 : 0;
      this.$store.dispatch('toggleDroplets', true);
      if ($puddle) {
        setTimeout(() => {
          const level = $puddle.getAttribute('drp-level');
          this.$store.dispatch('setPuddleFilter', level);
        }, time);
      }
    },
    clickSave() {
      // init any callbacks for clicking save within each droplet bubble
      this.afterActionCheck(this.$children, 'drzClickSave');
      this.toggleBubble();
    },
    async revertDroplet() {
      this.$log.info('revertDroplet from droplet-options');
      const revert = {
        drp: this.droplet,
        initialState: this.initialState,
      };
      if (this.canvasEditor) {
        revert.items = this.currentSite.recycled;
        revert.index = revert.items.findIndex(d => d._id === revert.drp._id);
      } else {
        revert.items = this.items;
        revert.index = this.index;
      }
      const droplet = await this.$store.dispatch('revertDroplet', revert);

      if (this.canvasEditor) {
        await this.$store.dispatch('setCanvasPage', {
          drp: droplet,
        });
      }
      // we utilize a bounce state to run callbacks inside the cancelled
      // droplet. In case we need to re-run plugins or regenerate css.
      // we need to exclude the code droplet as there is a weird template
      // condition that does not allow the overlay bubble to move back in
      // during the bounce
      if (this.droplet.type !== 'droplet.codeGeneral') {
        await this.$store.dispatch('bounceDroplet', { drp: droplet });
      }
      // run all callbacks on the children
      await this.afterActionCheck(this.$children, 'drzRevertData');
    },
    highlight(ctx, query, col) {
      const c = this.cleanItem(ctx, col);
      return c.replace(
        new RegExp(query, 'ig'),
        match => `<span class='editorUi-searchHighlight'>${match}</span>`,
      );
    },
    setDragType(type) {
      window.drzzle.dragType = type;
    },
    cleanItem(item, col) {
      let i = item || '--';
      if (col === 'createdDate' && i !== '--') {
        i = this._zoneFormat({
          date: i,
          format: 'MM-DD-YYYY hh:mm A z',
          zone: this.currentSite.timezone,
        });
      }
      return i;
    },
    seperateCamalCase(str, mobile) {
      const tail = mobile ? ': ' : '';
      return str.replace(/([A-Z])/g, g => ` ${g[0].toLowerCase()}`) + tail;
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/_bubble-options/index.js